import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Combobox } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { CalendarPicker } from '../../components/filters/CalendarPicker';
import { pricingApi } from '../../lib/pricingApi';
import { useOptions } from '../../hooks/useOptions';
import { useAuth } from '../../hooks/useAuth';
import { isAdminOrAbove } from '../../lib/roles';
import { csvCell, downloadText } from '../../utils/exportCsv';
import { SubProductPanel } from './SubProductPanel';
import { ProductList } from './ProductList';
import { moneyLabel, SYMBOL, CURRENCIES, CURRENCY_LABEL, DEFAULT_CURRENCY, priceCurrency, STATUSES, STATUS_TONE } from './helpers';

const field = 'rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40 dark:border-white/15 dark:bg-slate-800 dark:text-slate-200';

// Draft value for a row: what the manager has typed, else what is stored.
// A stored price of null yields '' — an unconfigured row shows an EMPTY box, so
// it can never be mistaken for a negotiated price that happens to equal base.
const draftOf = (edits, s) => (edits[s.id] !== undefined
  ? edits[s.id]
  : (s.resellerPrice === null ? '' : String(s.resellerPrice)));

const changed = (typed, stored) => {
  if (typed === undefined) return false;
  const t = String(typed).trim();
  if (t === '') return stored !== null;
  if (stored === null) return true;
  return Number(t) !== Number(stored);
};

// The currency a reseller is priced in. One per reseller, so it is read off the
// first price they actually have; USD until they have any.
const currencyOfTree = (tree, fallback) => {
  for (const p of tree) {
    for (const s of p.subProducts) {
      if (s.isConfigured) return priceCurrency(s);
    }
  }
  return fallback || DEFAULT_CURRENCY;
};

const configuredIn = (tree) => tree.reduce(
  (n, p) => n + p.subProducts.filter((s) => s.isConfigured).length, 0,
);

const rememberedCurrency = (reseller) => {
  try { return localStorage.getItem(`pricing.currency.${reseller}`) || ''; } catch { return ''; }
};

// Availability of the sub-product itself. A coloured dot plus a word reads
// faster than a solid badge and keeps a column of ten rows calm.
const AVAIL_STYLE = {
  Available: ['bg-green-500', 'text-green-700 dark:text-green-400'],
  'Pre-Order': ['bg-brand', 'text-brand-dark dark:text-brand-light'],
  'Out of Stock': ['bg-red-500', 'text-red-600 dark:text-red-400'],
  Discontinued: ['bg-slate-400', 'text-slate-500 dark:text-slate-400'],
};

function Availability({ status }) {
  const [dot, text] = AVAIL_STYLE[status] || AVAIL_STYLE.Discontinued;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium ${text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
}

export default function ProductPricing() {
  const { profile } = useAuth();
  const canEdit = isAdminOrAbove(profile);
  const { resellers } = useOptions();

  // Remembered across visits. The reseller roster comes from BigQuery and costs
  // a round trip; reading the last choice synchronously means the first fetch
  // goes out with the right reseller straight away instead of waiting for that
  // list, or firing once for "all" and again for the default.
  const [reseller, setReseller] = useState(() => {
    try { return localStorage.getItem('pricing.reseller') || ''; } catch { return ''; }
  });
  const [tree, setTree] = useState([]);
  // Starts true: the first fetch is gated on the reseller list arriving, and a
  // false start would flash the "No products yet" empty state before any request
  // had even been made.
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [subQuery, setSubQuery] = useState('');
  const [edits, setEdits] = useState({});
  // The selected reseller's currency — every price of theirs is in it. Set from
  // the toolbar, defaulted to USD, and re-read from their saved prices on load.
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [pendingCurrency, setPendingCurrency] = useState(null);   // awaiting confirmation
  const [switching, setSwitching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openSub, setOpenSub] = useState(null);

  const [addingProduct, setAddingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', status: 'Available', description: '' });
  const [addingSub, setAddingSub] = useState(false);
  const [newSub, setNewSub] = useState({ name: '', sku: '', status: 'Available', basePrice: '', currency: 'USD', effectiveDate: '', notes: '' });
  const [busy, setBusy] = useState(false);

  // A reseller account is pinned to its own prices server-side; the picker is an
  // admin control and is not rendered for them.
  useEffect(() => {
    if (!profile || canEdit) return;
    setReseller(profile.reseller_name || '');
  }, [profile, canEdit]);

  // Default to the first reseller so the price column has a subject on arrival —
  // an empty column is the least useful thing this page could open with. Also
  // drops a remembered reseller that no longer exists in the roster.
  useEffect(() => {
    if (!canEdit || !resellers.length) return;
    if (!reseller || !resellers.some((r) => r === reseller)) setReseller(resellers[0]);
  }, [canEdit, reseller, resellers]);

  useEffect(() => {
    if (!canEdit) return;
    try { localStorage.setItem('pricing.reseller', reseller); } catch { /* private mode */ }
  }, [reseller, canEdit]);

  const load = useCallback(() => {
    if (!profile) return;
    // Deliberately NOT gated on the reseller roster. That list comes from
    // BigQuery, and waiting for it left the page blank for the whole round trip
    // even though the pricing itself (Postgres) was ready far sooner. With a
    // remembered reseller this fires once with the right one; without one it
    // paints the products immediately and refines when the default arrives —
    // and `refreshing` below keeps that second pass from blanking anything.
    setLoading(true);
    pricingApi.tree(canEdit ? reseller : undefined)
      .then((t) => {
        setTree(t);
        setEdits({});
        // Their saved prices are the source of truth; the remembered pick only
        // covers a reseller who has no prices yet.
        setCurrency(currencyOfTree(t, rememberedCurrency(canEdit ? reseller : '')));
      })
      .catch((e) => setMsg({ type: 'error', text: e.message }))
      .finally(() => setLoading(false));
  }, [reseller, canEdit, profile]);

  useEffect(() => { load(); }, [load]);

  // Keep a selection alive across reloads; fall back to the first product.
  useEffect(() => {
    if (!tree.length) { setSelectedId(null); return; }
    if (!tree.some((p) => p.id === selectedId)) setSelectedId(tree[0].id);
  }, [tree, selectedId]);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  // Blank the page only when there is nothing to show yet. A reseller switch
  // refetches, but the grid it is replacing stays put and just dims — far
  // steadier than tearing the whole layout down for every change.
  const firstLoad = loading && !tree.length;
  const refreshing = loading && tree.length > 0;

  const product = useMemo(() => tree.find((p) => p.id === selectedId) || null, [tree, selectedId]);

  const rows = useMemo(() => {
    if (!product) return [];
    const q = subQuery.trim().toLowerCase();
    return product.subProducts.filter((s) => (q
      ? [s.name, s.sku, s.notes].some((f) => String(f || '').toLowerCase().includes(q))
      : true));
  }, [product, subQuery]);

  const dirty = useMemo(
    () => (product ? product.subProducts.filter((s) => changed(edits[s.id], s.resellerPrice)) : []),
    [product, edits],
  );

  const saveAll = async () => {
    if (!dirty.length) return;
    setSaving(true);
    try {
      const payload = dirty.map((s) => {
        const t = String(draftOf(edits, s)).trim();
        // Every price saved here carries the reseller's currency, so a new one
        // can never land in a different currency from the rest of their list.
        return { subProductId: s.id, price: t === '' ? null : Number(t), currency };
      });
      const res = await pricingApi.bulkSet(reseller, payload);
      if (res.failed.length) {
        flash('error', `${res.applied.length} saved, ${res.failed.length} failed: ${res.failed[0].error}`);
      } else {
        flash('success', `Saved ${res.applied.length} price${res.applied.length === 1 ? '' : 's'} for ${reseller} in ${currency}.`);
      }
      load();
    } catch (e) { flash('error', e.message); } finally { setSaving(false); }
  };

  const configured = useMemo(() => configuredIn(tree), [tree]);

  // Prices left in another currency — from before the reseller had one, or from
  // a row added on another screen. Switching the picker sweeps them all in.
  const mixed = useMemo(() => tree.reduce((n, p) => n
    + p.subProducts.filter((s) => s.isConfigured && priceCurrency(s) !== currency).length, 0), [tree, currency]);

  // Picking a currency relabels every price this reseller has, so it is
  // confirmed first — unless they have no prices yet, where there is nothing to
  // relabel and the pick is simply remembered for the next one saved.
  const chooseCurrency = (next) => {
    if (!next || next === currency) return;
    if (configured > 0) { setPendingCurrency(next); return; }
    setCurrency(next);
    try { localStorage.setItem(`pricing.currency.${reseller}`, next); } catch { /* private mode */ }
  };

  const applyCurrency = async () => {
    const next = pendingCurrency;
    setSwitching(true);
    try {
      const res = await pricingApi.setResellerCurrency(reseller, next);
      setCurrency(next);
      try { localStorage.setItem(`pricing.currency.${reseller}`, next); } catch { /* private mode */ }
      setPendingCurrency(null);
      flash('success', `${reseller} is now priced in ${next} — ${res.updated} price${res.updated === 1 ? '' : 's'} relabelled.`);
      load();
    } catch (e) { flash('error', e.message); } finally { setSwitching(false); }
  };

  const addProduct = async () => {
    setBusy(true);
    try {
      const p = await pricingApi.createProduct(newProduct);
      setAddingProduct(false);
      setNewProduct({ name: '', status: 'Available', description: '' });
      flash('success', `Product "${p.name}" added.`);
      setSelectedId(p.id);
      load();
    } catch (e) { flash('error', e.message); } finally { setBusy(false); }
  };

  const addSubProduct = async () => {
    setBusy(true);
    try {
      const s = await pricingApi.createSubProduct(product.id, {
        name: newSub.name,
        sku: newSub.sku || null,
        status: newSub.status,
        basePrice: newSub.basePrice === '' ? null : Number(newSub.basePrice),
        currency: newSub.currency,
        effectiveDate: newSub.effectiveDate || null,
        notes: newSub.notes || null,
      });
      setAddingSub(false);
      setNewSub({ name: '', sku: '', status: 'Available', basePrice: '', currency: 'USD', effectiveDate: '', notes: '' });
      flash('success', `"${s.name}" added to ${product.name}.`);
      load();
    } catch (e) { flash('error', e.message); } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const m = await pricingApi.matrix();
      // Each reseller gets a figure column AND a currency column beside it: the
      // figures are raw numbers so the sheet can still sum and sort them, and a
      // 40 in ILS next to a 40 in USD must not read as the same money.
      const header = ['Product', 'Sub-product', 'SKU', 'Status', 'Effective Date', 'Base Price', 'Base Currency',
        ...m.resellers.flatMap((r) => [r, `${r} Currency`])];
      const lines = [header.join(',')].concat(m.items.map((s) => [
        s.product, s.name, s.sku, s.status, s.effectiveDate, s.basePrice, s.currency,
        // Blank, not 0, where a reseller has no configured price.
        ...m.resellers.flatMap((r) => (s.prices[r] === null
          ? ['', '']
          : [s.prices[r], s.currencies?.[r] || s.currency])),
      ].map(csvCell).join(',')));
      downloadText('product-pricing.csv', lines.join('\n'), 'text/csv');
    } catch (e) { flash('error', e.message); }
  };

  return (
    <AppShell title="Product Pricing">
      {msg && (
        <div className={`rounded-xl px-4 py-2.5 text-sm ${msg.type === 'error'
          ? 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300'
          : 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300'}`}
        >
          {msg.text}
        </div>
      )}

      {/* AppShell already renders the page title in the top bar, so this row
          carries only the one-line purpose and the create actions. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Manage sub-products and set prices for each reseller.
          {refreshing && <span className="ml-2 text-brand dark:text-brand-light">Updating…</span>}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={exportCsv} disabled={!tree.length}>Export</Button>
          {canEdit && (
            <>
              <Button variant="secondary" onClick={() => setAddingSub(true)} disabled={!product} className="gap-1.5">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 4v12M4 10h12" strokeLinecap="round" /></svg>
                Add Sub-product
              </Button>
              <Button onClick={() => setAddingProduct(true)} className="gap-1.5">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 4v12M4 10h12" strokeLinecap="round" /></svg>
                Add Product
              </Button>
            </>
          )}
        </div>
      </div>

      {firstLoad ? <Spinner label="Loading pricing…" /> : !tree.length ? (
        <div className="rounded-2xl bg-white/70 py-16 text-center text-sm text-slate-400 ring-1 ring-slate-900/5 dark:bg-white/5 dark:text-slate-500 dark:ring-white/10">
          No products yet.
        </div>
      ) : (
        // Master/detail: the product rail stays put while the grid changes, so
        // moving between vendors never costs a page reload or a scroll back up.
        <div className={`grid grid-cols-1 gap-4 transition-opacity lg:grid-cols-[280px,minmax(0,1fr)] ${refreshing ? 'opacity-60' : ''}`}>
          <ProductList
            products={tree}
            selectedId={selectedId}
            onSelect={(id) => { setSelectedId(id); setSubQuery(''); }}
            reseller={reseller}
          />

          <div className="min-w-0 space-y-4">
            {/* Selected product header */}
            {product && (
              <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-white/80 px-5 py-4 shadow-sm ring-1 ring-slate-900/5 dark:bg-slate-900/60 dark:ring-white/10">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-gradient text-base font-bold text-white">
                  {product.name.slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 dir="auto" className="truncate text-lg font-semibold text-navy-800 dark:text-slate-100">{product.name}</h2>
                    <Badge tone={STATUS_TONE[product.status] || 'neutral'}>{product.status}</Badge>
                  </div>
                  <p className="mt-0.5 truncate text-sm text-slate-500 dark:text-slate-400">
                    {product.description || `${product.subProducts.length} sub-product${product.subProducts.length === 1 ? '' : 's'} in this product`}
                  </p>
                </div>
                <div className="flex items-center overflow-hidden rounded-lg ring-1 ring-slate-200 dark:ring-white/10">
                  <span className="bg-slate-100 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-white/10 dark:text-slate-400">
                    Sub-products
                  </span>
                  <span className="px-3 py-1.5 text-base font-bold text-navy-800 dark:text-slate-100">
                    {product.subProducts.length}
                  </span>
                </div>
              </div>
            )}

            {/* Sub-product grid */}
            <div className="overflow-hidden rounded-2xl bg-white/80 shadow-sm ring-1 ring-slate-900/5 dark:bg-slate-900/60 dark:ring-white/10">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-white/10">
                <h3 className="font-semibold text-navy-800 dark:text-slate-100">Sub-products</h3>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {product ? `${rows.length} of ${product.subProducts.length} shown` : ''}
                </span>
              </div>

              {/* One toolbar row: WHOSE prices these are on the left — the most
                  consequential choice on the page — and the filter on the right. */}
              <div className="px-5 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50/80 px-3 py-2.5 ring-1 ring-inset ring-slate-200/70 dark:bg-white/5 dark:ring-white/10">
                  <div className="flex items-center gap-2.5">
                    <svg className="h-4 w-4 shrink-0 text-brand-dark/70 dark:text-brand-light/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M16 19v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1M9.5 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM21 19v-1a4 4 0 00-3-3.87" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {canEdit ? 'Setting prices for' : 'Your prices'}
                    </span>
                    {canEdit ? (
                      <div className="w-[220px]">
                        {/* No "All resellers" entry: this picks WHOSE prices the
                            editable column belongs to, and there is no such thing
                            as editing everyone's at once. That view is the Export. */}
                        <Combobox
                          value={reseller}
                          onChange={setReseller}
                          options={resellers}
                          placeholder="Select a reseller…"
                          searchPlaceholder="Search resellers…"
                        />
                      </div>
                    ) : (
                      <span className="text-base font-semibold text-navy-800 dark:text-slate-100">{reseller || '—'}</span>
                    )}

                    {/* The reseller's currency — one for their whole price list,
                        so it belongs beside their name and not on every row.
                        Nothing is converted: this is the label the figures carry. */}
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">in</span>
                    {canEdit && reseller ? (
                      <select
                        value={currency}
                        onChange={(e) => chooseCurrency(e.target.value)}
                        aria-label={`Currency for ${reseller}`}
                        title={`Every price for ${reseller} is recorded in this currency`}
                        className="shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-navy-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 dark:border-white/15 dark:bg-slate-800 dark:text-slate-100"
                      >
                        {CURRENCIES.map((c) => <option key={c} value={c}>{SYMBOL[c]} {CURRENCY_LABEL[c] || c}</option>)}
                      </select>
                    ) : (
                      <span className="shrink-0 text-base font-semibold text-navy-800 dark:text-slate-100">
                        {SYMBOL[currency]} {currency}
                      </span>
                    )}
                    {canEdit && mixed > 0 && (
                      <button
                        type="button"
                        onClick={() => setPendingCurrency(currency)}
                        className="rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 underline-offset-2 hover:underline dark:bg-amber-500/15 dark:text-amber-300"
                      >
                        {mixed} price{mixed === 1 ? '' : 's'} in another currency — set {mixed === 1 ? 'it' : 'them'} to {currency}
                      </button>
                    )}
                  </div>

                  <div className="relative w-full max-w-xs">
                    <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="9" cy="9" r="6" /><path d="M14 14l3 3" strokeLinecap="round" /></svg>
                    <input
                      value={subQuery}
                      onChange={(e) => setSubQuery(e.target.value)}
                      placeholder="Search sub-products…"
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 dark:border-white/15 dark:bg-slate-800 dark:text-slate-200"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                {/* table-fixed + colgroup: SKU widths differ wildly between
                    products (SentinelOne has none, N-Able has 7 digits), so
                    without fixed columns every product switch reflowed the whole
                    grid and the headers drifted off their values. */}
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    <col className="w-[44px]" />
                    <col />
                    <col className="w-[96px]" />
                    <col className="w-[104px]" />
                    <col className="w-[132px]" />
                    <col className="w-[112px]" />
                    <col className="w-[64px]" />
                  </colgroup>
                  <thead>
                    <tr className="border-y border-slate-100 bg-slate-50/70 text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                      <th className="py-2.5 pl-5 pr-2 text-right text-[11px] font-semibold uppercase tracking-wider">#</th>
                      <th className="py-2.5 px-3 text-left text-[11px] font-semibold uppercase tracking-wider">Sub-product</th>
                      <th className="py-2.5 px-3 text-left text-[11px] font-semibold uppercase tracking-wider">SKU</th>
                      <th className="border-l border-slate-100 py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider dark:border-white/5">Base price</th>
                      <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider">
                        {reseller ? `${reseller} price${currency ? ` · ${currency}` : ''}` : 'Reseller pricing'}
                      </th>
                      <th className="py-2.5 px-3 text-left text-[11px] font-semibold uppercase tracking-wider">Status</th>
                      <th className="py-2.5 pr-5" />
                    </tr>
                  </thead>
                  <tbody>
                    {!rows.length && (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">
                          {product?.subProducts.length ? 'No sub-products match your search.' : 'This product has no sub-products yet.'}
                        </td>
                      </tr>
                    )}
                    {rows.map((s, i) => {
                      const typed = draftOf(edits, s);
                      const isDirty = changed(edits[s.id], s.resellerPrice);
                      // One currency for the whole reseller, from the toolbar.
                      const cur = currency;

                      return (
                        <tr
                          key={s.id}
                          // Zebra striping: the name column absorbs the slack on
                          // a wide screen, leaving a long gap before SKU. Banding
                          // is what lets the eye carry a row across it.
                          className={`transition-colors ${isDirty
                            ? 'bg-amber-50/80 dark:bg-amber-500/10'
                            : i % 2
                              ? 'bg-slate-100/70 hover:bg-brand/[0.07] dark:bg-white/[0.04] dark:hover:bg-white/[0.06]'
                              : 'hover:bg-brand/[0.06] dark:hover:bg-white/[0.05]'}`}
                        >
                          <td className="py-2 pl-5 pr-2 text-right text-xs tabular-nums text-slate-400 dark:text-slate-500">{i + 1}</td>
                          <td className="py-2 px-3">
                            <button
                              type="button"
                              onClick={() => setOpenSub(s.id)}
                              dir="auto"
                              className="block w-full truncate text-left font-medium text-navy-800 hover:text-brand dark:text-slate-100 dark:hover:text-brand-light"
                            >
                              {s.name}
                            </button>
                            {s.notes && (
                              <div dir="auto" className="mt-0.5 truncate text-xs italic text-slate-400 dark:text-slate-500">{s.notes}</div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-slate-500 dark:text-slate-400">
                            <span className={`block truncate text-xs tabular-nums ${s.sku ? 'text-brand-dark dark:text-brand-light' : ''}`}>{s.sku || '—'}</span>
                          </td>
                          <td className="border-l border-slate-100 py-2 px-3 text-right tabular-nums text-slate-600 dark:border-white/5 dark:text-slate-300">
                            {moneyLabel(s.basePrice, s.currency) || '—'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {canEdit && reseller ? (
                              // The box fills the column, so BOTH its edges line
                              // up with the header above it. A fixed-width box
                              // only ever matched on one side.
                              <div className="relative w-full">
                                {/* The chip hugs the figure instead of anchoring
                                    to the far left — a mark at one edge and a dash
                                    at the other read as an empty box, not a price.
                                    It carries the CODE rather than the symbol:
                                    ILS and NIS both print ₪, and a price must
                                    never be ambiguous about which one it is in.
                                    It is the reseller's currency, set once on the
                                    toolbar rather than per row. */}
                                <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center rounded-l-lg border-r border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-500">
                                  {cur}
                                </span>
                                <input
                                  type="number" step="0.01" min="0" inputMode="decimal"
                                  value={typed}
                                  onChange={(e) => setEdits((p) => ({ ...p, [s.id]: e.target.value }))}
                                  placeholder=""
                                  aria-label={`${reseller} price for ${s.name}`}
                                  // A set row gains weight so the priced rows
                                  // carry the column; an unset one stays quiet.
                                  className={`no-spin w-full rounded-lg border bg-white py-1.5 pl-12 pr-2.5 text-right text-sm tabular-nums transition-colors hover:border-brand/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-800 ${isDirty
                                    // Amber marks the row as unsaved; the FIGURE
                                    // stays black. Tinting the number itself made
                                    // the price look like a warning rather than
                                    // the value you just typed.
                                    ? 'border-amber-400 bg-amber-50 font-semibold text-navy-800 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-slate-100'
                                    : typed === ''
                                      ? 'border-slate-200 text-slate-800 dark:border-white/15 dark:text-slate-100'
                                      // A figure that is set reads as a chip, so
                                      // the priced rows carry the column at a
                                      // glance and an empty box stays obviously
                                      // empty — never a stand-in for zero.
                                      : 'border-brand/30 bg-brand/[0.08] font-semibold text-brand-dark dark:border-brand/40 dark:bg-brand/20 dark:text-brand-light'}`}
                                  />
                              </div>
                            ) : reseller ? (
                              <span className="tabular-nums font-semibold text-navy-800 dark:text-slate-100">
                                {moneyLabel(s.resellerPrice, cur) || <span className="text-xs font-normal text-slate-400">No price</span>}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 dark:text-slate-500">
                                {s.configuredCount > 0 ? `${s.configuredCount} reseller${s.configuredCount === 1 ? '' : 's'}` : 'None'}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            <Availability status={s.status} />
                          </td>
                          <td className="py-2 pr-5">
                            {/* Still a plain button, not a menu — one click opens
                                the details dialog. The title carries what the
                                dropped label used to say. */}
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => setOpenSub(s.id)}
                                title={`${s.name} — details, reseller pricing and history`}
                                aria-label={`Edit ${s.name}`}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 ring-1 ring-slate-200 transition-colors hover:bg-brand/10 hover:text-brand-dark hover:ring-brand/30 focus:outline-none focus:ring-2 focus:ring-brand/40 dark:text-slate-500 dark:ring-white/15 dark:hover:bg-brand/20 dark:hover:text-brand-light"
                              >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                                  <circle cx="12" cy="12" r="3" />
                                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 3.6 1.65 1.65 0 0010 2.09V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 8v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer: what is on screen, and the save/reset pair. */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/70 px-5 py-3 dark:border-white/10 dark:bg-white/[0.03]">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {product && <>Showing {rows.length} of {product.subProducts.length} sub-products for <strong className="text-slate-600 dark:text-slate-300">{product.name}</strong></>}
                  {dirty.length > 0 && (
                    <strong className="ml-2 text-amber-600 dark:text-amber-400">
                      {dirty.length} unsaved change{dirty.length === 1 ? '' : 's'}
                    </strong>
                  )}
                </span>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" onClick={() => setEdits({})} disabled={!dirty.length || saving}>Reset</Button>
                    <Button onClick={saveAll} disabled={!dirty.length || saving}>
                      {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}


      <SubProductPanel
        subProductId={openSub}
        open={Boolean(openSub)}
        onClose={() => setOpenSub(null)}
        canEdit={canEdit}
        resellers={resellers}
        focusReseller={reseller}
        focusCurrency={currency}
        onChanged={load}
      />

      {/* Changing a reseller's currency rewrites the label on every price they
          have. Cheap to undo, but not something to do by brushing past a
          dropdown — so it is spelled out and confirmed. */}
      <Modal open={Boolean(pendingCurrency)} onClose={() => setPendingCurrency(null)} title={`Price ${reseller} in ${pendingCurrency}?`}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          All {configured} price{configured === 1 ? '' : 's'} for <strong>{reseller}</strong> will be recorded in{' '}
          <strong>{SYMBOL[pendingCurrency]} {CURRENCY_LABEL[pendingCurrency] || pendingCurrency}</strong>
          {pendingCurrency === currency
            ? <>, including the {mixed} still in another currency.</>
            : <> instead of <strong>{SYMBOL[currency]} {CURRENCY_LABEL[currency] || currency}</strong>.</>}
        </p>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          The figures do not change — a price of {moneyLabel(1234.5, currency)} becomes {moneyLabel(1234.5, pendingCurrency)},
          not a converted amount. Every price this reseller has, on every product, uses this currency.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPendingCurrency(null)} disabled={switching}>Cancel</Button>
          <Button onClick={applyCurrency} disabled={switching}>
            {switching ? 'Applying…' : `Price in ${pendingCurrency}`}
          </Button>
        </div>
      </Modal>

      <Modal open={addingProduct} onClose={() => setAddingProduct(false)} title="Add product">
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Product name</span>
            <input value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="e.g. Adlumin" className={`${field} w-full`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Status</span>
            <select value={newProduct.status} onChange={(e) => setNewProduct({ ...newProduct, status: e.target.value })} className={`${field} w-full`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Description</span>
            <input value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} placeholder="Optional" className={`${field} w-full`} />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setAddingProduct(false)}>Cancel</Button>
          <Button onClick={addProduct} disabled={busy || !newProduct.name.trim()}>{busy ? 'Adding…' : 'Add product'}</Button>
        </div>
      </Modal>

      <Modal open={addingSub} onClose={() => setAddingSub(false)} title={`Add sub-product to ${product?.name || ''}`}>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Sub-product name</span>
            <input value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} placeholder="e.g. Singularity Complete" className={`${field} w-full`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">SKU</span>
            <input value={newSub.sku} onChange={(e) => setNewSub({ ...newSub, sku: e.target.value })} placeholder="Optional" className={`${field} w-full`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Status</span>
            <select value={newSub.status} onChange={(e) => setNewSub({ ...newSub, status: e.target.value })} className={`${field} w-full`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Base price</span>
            <input type="number" step="0.01" min="0" value={newSub.basePrice} onChange={(e) => setNewSub({ ...newSub, basePrice: e.target.value })} placeholder="Optional" className={`${field} no-spin w-full text-right tabular-nums`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Base currency</span>
            <select value={newSub.currency} onChange={(e) => setNewSub({ ...newSub, currency: e.target.value })} className={`${field} w-full`}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{SYMBOL[c]} {c}</option>)}
            </select>
          </label>
          <div className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Effective date</span>
            <CalendarPicker value={newSub.effectiveDate} onChange={(v) => setNewSub({ ...newSub, effectiveDate: v })} portal placeholder="Not set" clearLabel="Clear" />
          </div>
          <label className="col-span-2 block">
            <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Notes</span>
            <input value={newSub.notes} onChange={(e) => setNewSub({ ...newSub, notes: e.target.value })} placeholder="Optional" className={`${field} w-full`} />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setAddingSub(false)}>Cancel</Button>
          <Button onClick={addSubProduct} disabled={busy || !newSub.name.trim()}>{busy ? 'Adding…' : 'Add sub-product'}</Button>
        </div>
      </Modal>
    </AppShell>
  );
}
